import moment from "moment";
import { DATE_RANGE_PRESETS } from "../Shared/DateRangePicker";

/** AppMenu code of the screen (Users › Access rights): Edit = reply to a rating. */
export const MENU_CODE = "rating";

/** Rating.RatingType: what the crew member rated. */
export const RATING_TYPES = {
  1: { label: "Hotel", icon: "fas fa-hotel" },
  2: { label: "Local vendor", icon: "fas fa-car" },
  3: { label: "Hotel shuttle", icon: "fas fa-shuttle-van" },
};

/** Score fields in the order the crew app asks them; a field rated 0 was not asked for that type. */
export const CATEGORIES = [
  { field: "service", label: "Service" },
  { field: "cleanliness", label: "Cleanliness" },
  { field: "punctuality", label: "Punctuality" },
  { field: "quality", label: "Quality" },
  { field: "location", label: "Location" },
  { field: "food", label: "Food" },
  { field: "bed", label: "Bed" },
];

export const REPLY_LENGTH = 1000;

export const FILTERS = [
  { id: "all", label: "All ratings", icon: "fas fa-star", tone: "primary" },
  { id: "1", label: "Hotel", icon: RATING_TYPES[1].icon, tone: "primary" },
  { id: "3", label: "Hotel shuttle", icon: RATING_TYPES[3].icon, tone: "primary" },
  { id: "2", label: "Local vendor", icon: RATING_TYPES[2].icon, tone: "primary" },
  { id: "low", label: "Low (2 or less)", icon: "fas fa-exclamation-triangle", tone: "muted" },
  { id: "unreplied", label: "Not replied", icon: "fas fa-reply", tone: "muted" },
];

/** Staff reply to ratings is hidden for now (user, 2026-10-30); set true to open it again (API and Edit right are ready). */
export const RATING_REPLY_ENABLED = false;

/** The filter cards shown: "Not replied" only while replies are open. */
export const visibleFilters = (replyEnabled = RATING_REPLY_ENABLED) => FILTERS.filter((f) => replyEnabled || f.id !== "unreplied");

/** Date presets of the filter: the usual ones plus longer ranges (crew rate rarely). */
export const RANGE_PRESETS = [
  ...DATE_RANGE_PRESETS,
  { label: "Last 3 Months", getRange: () => [moment().subtract(3, "month").add(1, "day"), moment()] },
  { label: "Last 12 Months", getRange: () => [moment().subtract(12, "month").add(1, "day"), moment()] },
];

/** First range shown: the last 3 months up to today. */
export const defaultRange = (today = moment()) => [
  moment(today).subtract(3, "month").add(1, "day").startOf("day").toDate(),
  moment(today).startOf("day").toDate(),
];

export const typeLabel = (row) => RATING_TYPES[row?.ratingType]?.label ?? "Other";

/** The categories rated for this row: [{ field, label, score }]. */
export const ratedCategories = (row) => CATEGORIES.map((c) => ({ ...c, score: Number(row?.[c.field]) || 0 })).filter((c) => c.score > 0);

/** Average of the rated categories, one decimal (0 when nothing was rated). */
export function overallScore(row) {
  const rated = ratedCategories(row);
  if (!rated.length) return 0;
  return Math.round((rated.reduce((sum, c) => sum + c.score, 0) / rated.length) * 10) / 10;
}

export const isReplied = (row) => Boolean(row?.replyMsg?.trim());

export function matchesFilter(row, id) {
  switch (id) {
    case "all":
      return true;
    case "low":
      return overallScore(row) > 0 && overallScore(row) <= 2;
    case "unreplied":
      return !isReplied(row);
    default:
      return String(row.ratingType) === id;
  }
}

export const filterRows = (rows, id) => rows.filter((r) => matchesFilter(r, id));

/** Problem of a reply before sending it, or "". */
export function replyError(text) {
  const reply = (text ?? "").trim();
  if (!reply) return "Write the reply.";
  if (reply.length > REPLY_LENGTH) return `The reply can have ${REPLY_LENGTH} characters at most.`;
  return "";
}

/** "30/07/2026 08:21" from the API's "yyyy-MM-ddTHH:mm:ss" (shown as stored); "" when empty. */
export const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "");

/** The kinds of comment the crew member ticked: ["Praise", "Problem", ...]. */
export const commentKinds = (row) =>
  [
    ["isPraise", "Praise"],
    ["isProblem", "Problem"],
    ["isQuestion", "Question"],
    ["isSuggestion", "Suggestion"],
  ]
    .filter(([field]) => row?.[field])
    .map(([, label]) => label);
